import {Avatar, openDialog, Status} from 'platform/components';
import {Box, Clickable, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {BusinessCaseActivityResponseBody} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  DirectSaleActivityResponseBody,
  DirectSaleManualActivityKindSchema,
} from '@dms/api/warehouse';
import {
  ActivityDate,
  ActivitySidebarLink,
  getActivityIconColor,
  LogContactDialog,
  transformActivityItem,
} from '@dms/features/activity-panel';
import {openEditTaskFormDialog} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {useCanOpenTelephonyCall, usePermissions} from '@dms/shared';

interface DirectSaleActivityListProps {
  directSaleId: string;
  label: string;
  items: (DirectSaleActivityResponseBody | BusinessCaseActivityResponseBody)[];
  showEmpty?: boolean;
}

export function DirectSaleActivityList(props: DirectSaleActivityListProps) {
  const [canUpdate, canDelete, canReadTasks] = usePermissions({
    permissionKeys: ['directSaleActivityUpdate', 'directSaleActivityDelete', 'readTask'],
  });
  const canOpenCall = useCanOpenTelephonyCall();

  return (
    <VStack spacing={4}>
      <Text size="xSmall" color="secondary">
        {props.label}
      </Text>
      <Show when={props.showEmpty && props.items.length === 0}>
        <Status headline={i18n.t('entity.activityPanel.activity.noNextSteps')} />
      </Show>
      {props.items.map((item) => {
        const isManual = DirectSaleManualActivityKindSchema.safeParse(item.activityType).success;
        const isTaskActivity =
          item.activityType === 'TASK_CREATED' || item.activityType === 'TASK_FINISHED';
        const transformed = transformActivityItem(
          item,
          undefined,
          {
            canOpenCall,
            manualActivity: {
              isEditable: isManual && item.isEditable && (canUpdate || canDelete),
              onClick: () => {
                if (
                  !isManual ||
                  !item.isEditable ||
                  (!canUpdate && !canDelete) ||
                  'interestId' in item
                ) {
                  return;
                }
                openDialog(
                  <LogContactDialog directSaleId={props.directSaleId} directSaleActivity={item} />,
                  {
                    id: 'LOG_CONTACT_DIALOG',
                    title: i18n.t('entity.activityPanel.logContact.editLog'),
                    scrollBehavior: 'outside',
                  }
                );
              },
            },
          },
          EntityResourceIds.directSale,
          (id) => {
            if (canReadTasks) {
              openEditTaskFormDialog({id, 'data-testid': 'direct-sale-activity-task-edit'});
            }
          }
        );
        if (!transformed) {
          return null;
        }
        return (
          <HStack key={item.id} spacing={2}>
            <Avatar
              color={transformed.color}
              icon={transformed.icon}
              iconColor={getActivityIconColor(transformed.color)}
              variant="square"
            />
            <Box flex={1} paddingBottom={4}>
              <VStack spacing={1}>
                <HStack justify="space-between">
                  <Clickable onClick={transformed.onClick} isDisabled={!transformed.isEditable}>
                    <Heading size={5}>{transformed.title}</Heading>
                  </Clickable>
                  <ActivityDate timestamp={item.timestamp} />
                </HStack>
                <Text size="xSmall" color="tertiary">
                  {transformed.interestType} {transformed.userName}
                </Text>
                <Show when={Boolean(transformed.description)}>
                  <Text size="xSmall" color="secondary">
                    {transformed.description}
                  </Text>
                </Show>
                <Show when={canReadTasks || !isTaskActivity}>
                  <HStack>
                    {transformed.links?.map((link) => (
                      <ActivitySidebarLink key={link.label} {...link} />
                    ))}
                  </HStack>
                </Show>
              </VStack>
            </Box>
          </HStack>
        );
      })}
    </VStack>
  );
}
